import { trackEvent } from 'helpers/analytics'
import {
  sampleCareerIdWithMust,
  sampleGoodFitSpaceId
} from 'helpers/sample-data'
import { useCareersQuery } from 'hooks/use-careers-query'
import { useAddSampleData } from 'hooks/use-sample-data'
import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { mutation } from '../../../graphql'
import { delayedAction, fetchCompanyBrand } from '../../../helpers'
import { MESSAGE_ROLES, MESSAGE_TYPES, ONBOARDING_MESSAGES } from '../utils'
import { startRequirementProfileAssistantFlow } from '../utils/requirementProfileAssistant'
import { useDiscover } from './useDiscover'
import { useSelectionParams } from './useSelectionParams'

const ASSESSMENT_PREVIEW_DESCRIPTION =
  '**Das geht schnell - und macht sogar Spaß:**\n\nUnsere Assessments sind **gamifiziert**, dauern nur **wenige Minuten** und erfassen **Fähigkeiten, Persönlichkeit, Werte und Interessen**.\n\nSo erhältst du **valide Daten nach wissenschaftlichen Standards** und bietest gleichzeitig eine **herausragende Candidate Experience**.'

const ASSESSMENT_PREVIEW_GIF_URL =
  'https://aivy-assets.s3.eu-central-1.amazonaws.com/challenges/tower-of-london/instruction.gif'

const MATCHING_PROFILE_VIDEO_DESCRIPTION =
  '\nJetzt kennt Aivy eure Anforderungen für die Stelle (Schritt 1) und die Ergebnisse eurer Talente (Schritt 2) und bringt diese für euch zusammen → Im Passungsprofil. So siehst du auf einem Blick, wer am besten zur Stelle und zu euren Anforderungen passt.'

/**
 * Triggert beim Onboarding die initiale Assistenten-Sequenz (Video, Brand-Vorschlag, Name-Abfrage).
 */
export function useOnboardingAssistant({
  variant,
  hasCompletedOnboarding,
  activeSpace,
  enableAssistant,
  hideAssistant,
  setAssistantIsTyping,
  sendMessageToAssistant,
  partner,
  refetchPartner,
  refetchActiveSpace,
  language,
  system,
  cognitoUser,
  run,
  setAssessmentPreviewModalOpen
}) {
  const hasRunRef = useRef(false)
  const { t } = useTranslation()

  const { addCareer, addTalent } = useSelectionParams()

  const {
    setAssistantSpotlightSelector,
    clearAssistantMessages,
    setEnableClickOnFirstDimension,
    setAssistantFocus
  } = useDiscover()

  // Legt die Beispieldaten für "Jetzt erleben" an, offengelegt in
  // discover.assistant_onboarding.1.description
  const { mutateAsync: addSampleData } = useAddSampleData({
    notify: false,
    source: 'discover_onboarding'
  })

  const { refetch: refetchCareers, data: careers } = useCareersQuery({
    select: (items) => {
      return items
        .filter(({ status }) => status === 'ACTIVE')
        .filter(({ id }) => !id.includes('PRODUCT_TOUR'))
        .filter(({ archived }) => !archived)
    }
  })

  const partnerId = partner?.id
  const logo = partner?.logo
  const DECLINE_STORAGE_KEY = 'company_profile_adoption_declined'

  const handleAssessmentPreviewCta = useCallback(() => {
    // TODO: refactor after profile is being loaded
    trackEvent('discover_assessment_preview_completed')
    setAssistantIsTyping(true)
    setAssessmentPreviewModalOpen(false)

    setTimeout(() => {
      addTalent(sampleGoodFitSpaceId(partnerId))

      enableAssistant()
      setAssistantIsTyping(false)
    }, 1000)
  }, [
    addTalent,
    enableAssistant,
    partnerId,
    sendMessageToAssistant,
    setAssistantIsTyping,
    setEnableClickOnFirstDimension
  ])

  const sendAssessmentPreviewMessage = useCallback(() => {
    setAssistantIsTyping(false)
    enableAssistant()
    setAssessmentPreviewModalOpen(true)

    sendMessageToAssistant({
      id: `assessment_preview_intro_${Date.now()}`,
      role: MESSAGE_ROLES.ASSISTANT,
      type: MESSAGE_TYPES.VIDEO,
      videoMessageProps: {
        isPulsing: true,
        title: t('discover.assistant_onboarding.7.title'),
        description: t('discover.assistant_onboarding.7.description'),
        videoSrc:
          language === 'de'
            ? 'https://aivy-assets.s3.eu-central-1.amazonaws.com/videos/discover-3-de.mp4'
            : 'https://aivy-assets.s3.eu-central-1.amazonaws.com/videos/discover-3-en.mp4',
        ctaPrimary: {
          title: t('discover.assistant_onboarding.7.cta'),
          onClick: handleAssessmentPreviewCta
        }
      }
    })
  }, [handleAssessmentPreviewCta, sendMessageToAssistant, setAssistantIsTyping])

  const uploadLogoFromUrl = useCallback(
    async (logoUrl) => {
      if (!logoUrl) return null
      try {
        const response = await fetch(logoUrl)
        const blob = await response.blob()
        const file = new File([blob], 'company-logo.png', { type: blob.type })
        const { Storage } = await import('aws-amplify')
        const ext = file.name.split('.').pop()
        const key = `partner-logos/${partner.id}.${ext}`
        const putResult = await Storage.put(key, file, {
          contentType: file.type
        })
        const uploadedUrl = (await Storage.get(putResult.key)).split('?')[0]
        return uploadedUrl
      } catch (error) {
        console.error('Error uploading logo from URL:', error)
        return null
      }
    },
    [partner]
  )

  const handleNameSubmit = useCallback(
    async ({ firstname, lastname }, onSuccess) => {
      try {
        await mutation({
          mutation: 'updatePerson',
          input: {
            id: activeSpace.person.id,
            firstname,
            lastname
          }
        })
        if (refetchActiveSpace) await refetchActiveSpace()
        sendMessageToAssistant({
          id: `name_success_${Date.now()}`,
          role: MESSAGE_ROLES.ASSISTANT,
          type: MESSAGE_TYPES.TEXT,
          title: 'Name gespeichert',
          description: `Hallo ${firstname} ${lastname}, dein Name wurde gespeichert.`
        })
        if (onSuccess) onSuccess()
      } catch (error) {
        console.error('Error updating person name:', error)
        sendMessageToAssistant({
          id: `name_error_${Date.now()}`,
          role: MESSAGE_ROLES.ASSISTANT,
          type: MESSAGE_TYPES.TEXT,
          title: 'Fehler',
          description: 'Der Name konnte nicht gespeichert werden.'
        })
      }
    },
    [activeSpace, refetchActiveSpace, sendMessageToAssistant]
  )

  const sendRequirementProfileVideoMessage = useCallback(() => {
    sendMessageToAssistant({
      id: `requirement_profile_message_${Date.now()}`,
      role: MESSAGE_ROLES.ASSISTANT,
      type: MESSAGE_TYPES.VIDEO,
      videoMessageProps: {
        isPulsing: true,
        title: t(ONBOARDING_MESSAGES[0].title),
        description: t(ONBOARDING_MESSAGES[0].descriptionMarkdownDe),
        videoSrc:
          language === 'de'
            ? 'https://aivy-assets.s3.eu-central-1.amazonaws.com/videos/discover-2-de.mp4'
            : 'https://aivy-assets.s3.eu-central-1.amazonaws.com/videos/discover-2-en.mp4',
        ctaPrimary: {
          title: t('discover.assistant_onboarding.2.cta'),
          onClick: () => {
            trackEvent('discover_requirement_profile_opened')
            hideAssistant()
            clearAssistantMessages()
            startRequirementProfileAssistantFlow({
              sendMessageToAssistant,
              setAssistantSpotlightSelector,
              onFinish: () => {
                setTimeout(() => {
                  clearAssistantMessages()
                  sendAssessmentPreviewMessage()
                }, 350)
              },
              t
            })
          }
        }
      }
    })
  }, [
    sendAssessmentPreviewMessage,
    sendMessageToAssistant,
    setAssistantSpotlightSelector
  ])

  const handleRequirementProfileFlow = useCallback(() => {
    if (!partnerId) {
      console.error('Missing partner id for requirement profile flow')
      return
    }

    trackEvent('discover_requirement_profile_started')

    setAssistantIsTyping(true)

    delayedAction(async () => {
      try {
        // addCareer braucht genau diese Stelle (aktiv, nicht archiviert,
        // siehe select oben), deshalb exakter ID-Vergleich statt isSampleCareer
        const careerId = sampleCareerIdWithMust(partnerId)
        const hasExampleCareer = (careers ?? []).some(
          ({ id }) => id === careerId
        )

        if (!hasExampleCareer) {
          await addSampleData()
          await refetchCareers()
        }

        addCareer(careerId)

        sendRequirementProfileVideoMessage()
      } catch (error) {
        console.error('Error preparing requirement profile flow:', error)
      } finally {
        setAssistantIsTyping(false)
      }
    }, 3000)
  }, [
    addCareer,
    addSampleData,
    careers,
    hideAssistant,
    partnerId,
    refetchCareers,
    sendRequirementProfileVideoMessage,
    setAssistantIsTyping
  ])

  const sendRequirementProfileIntroMessage = useCallback(() => {
    clearAssistantMessages()
    setAssistantIsTyping(false)

    sendMessageToAssistant({
      id: `requirement_profile_intro_${Date.now()}`,
      role: MESSAGE_ROLES.ASSISTANT,
      type: MESSAGE_TYPES.VIDEO,
      videoMessageProps: {
        isPulsing: true,
        title: t('discover.assistant_onboarding.1.title'),
        description: t('discover.assistant_onboarding.1.description'),
        videoSrc:
          language === 'de'
            ? 'https://aivy-assets.s3.eu-central-1.amazonaws.com/videos/discover-1-de.mp4'
            : 'https://aivy-assets.s3.eu-central-1.amazonaws.com/videos/discover-1-en.mp4',
        ctaPrimary: {
          title: t('discover.assistant_onboarding.1.cta'),
          onClick: handleRequirementProfileFlow
        }
      }
    })
  }, [
    handleRequirementProfileFlow,
    sendMessageToAssistant,
    setAssistantIsTyping
  ])

  useEffect(() => {
    if (
      hasRunRef.current ||
      variant !== 'onboarding' ||
      hasCompletedOnboarding ||
      run === false
    )
      return

    hasRunRef.current = true
    ;(async () => {
      enableAssistant()
      setAssistantIsTyping(true)

      const person = activeSpace?.person || {}
      const emailToUse = person.invitation_email || person.email || ''
      const domain = emailToUse.split('@')[1] || ''

      const { firstname, lastname } = person

      const hasName = firstname || lastname

      const brand = await fetchCompanyBrand(domain)

      let hasDeclined = false
      try {
        hasDeclined = localStorage.getItem(DECLINE_STORAGE_KEY) === '1'
      } catch (_) {}

      const shouldShowCompanyProfileAdoption = logo === null && !hasDeclined

      // const sendFinalVideoMessage = () => {
      //   setTimeout(() => {
      //     setAssistantIsTyping(false)
      //     sendMessageToAssistant({
      //       id: `onboarding_final_video_${Date.now()}`,
      //       role: MESSAGE_ROLES.ASSISTANT,
      //       type: MESSAGE_TYPES.VIDEO,
      //       videoMessageProps: {
      //         isPulsing: true,
      //         title: 'Was Nutzer:innen an Aivy lieben',
      //         description:
      //           'Wie einfach es ist, den Menschen hinter dem Lebenslauf zu entdecken. Lass uns das gemeinsam ausprobieren. Klicke "Loslegen" und wähle Beispieltalent: Charlie aus.',
      //         videoSrc:
      //           'https://aivy-assets.s3.eu-central-1.amazonaws.com/videos/Onboarding_2.mp4',
      //         ctaPrimary: {
      //           title: 'Loslegen',
      //           onClick: async () => {
      //             sendMessageToAssistant({
      //               id: 'onboarding_loading_demo_career',
      //               role: MESSAGE_ROLES.ASSISTANT,
      //               type: MESSAGE_TYPES.TEXT,
      //               title: 'Einen moment bitte...',
      //               description:
      //                 'Ich richte alles für dich ein. Das kann ein paar Sekunden dauern.'
      //             })
      //             setAssistantIsTyping(true)

      //             const careerId = await createDemoCareer({
      //               partner,
      //               preset,
      //               language,
      //               cognitoUser,
      //               system,
      //               forceRefetchCareers
      //             })

      //             setTimeout(() => {
      //               setAssistantIsTyping(false)
      //               addCareer(careerId)
      //               hideAssistant()
      //               setAssistantIsTyping(true)
      //               sendMessageToAssistant({
      //                 id: 'requirement_profile_intro',
      //                 role: MESSAGE_ROLES.ASSISTANT,
      //                 type: MESSAGE_TYPES.TEXT,
      //                 title: 'Anforderungen festlegen',
      //                 description:
      //                   'Lege die relevanten Kriterien für die Position fest – entweder gemeinsam mit der Fachabteilung oder direkt per bewährter, validierter Vorlage, z.B. für HR-Manager:innen. So startest du schnell und effizient.'
      //               })
      //               setAssistantIsTyping(false)
      //               setTimeout(() => {
      //                 setRunRequirementProfileJoyride(true)
      //               }, 5000)
      //             }, 2500)
      //           }
      //         }
      //       }
      //     })
      //   }, 1500)
      // }

      const sendNameInputMessage = () => {
        sendMessageToAssistant({
          id: `name_input_${Date.now()}`,
          role: MESSAGE_ROLES.ASSISTANT,
          type: MESSAGE_TYPES.NAME_INPUT,
          nameInputProps: {
            onSubmit: async (data) => {
              await handleNameSubmit(data, () => {
                setAssistantIsTyping(false)
                sendRequirementProfileIntroMessage()
              })
            },
            onSkip: () => {
              setAssistantIsTyping(false)
              sendRequirementProfileIntroMessage()
            }
          }
        })
        setAssistantIsTyping(false)
      }

      const sendCompanyProfileAdoptionMessage = async (brand) => {
        // Basis-Rate für accept/decline_company_profile (die tracken die
        // Message-Komponenten selbst) — "gezeigt" fehlte bisher.
        trackEvent('discover_show_company_profile')
        setTimeout(() => {
          setAssistantIsTyping(false)
          sendMessageToAssistant({
            id: `company_profile_adoption_${Date.now()}`,
            role: MESSAGE_ROLES.ASSISTANT,
            type: MESSAGE_TYPES.COMPANY_PROFILE_ADOPTION,
            companyProfileAdoptionProps: {
              brandData: brand,
              onAdopt: async () => {
                setAssistantIsTyping(true)
                const logoUrl = brand?.searchData?.[0]?.icon
                let uploadedLogoUrl = null
                if (logoUrl) {
                  uploadedLogoUrl = await uploadLogoFromUrl(logoUrl)
                  if (uploadedLogoUrl) {
                    await mutation({
                      mutation: 'updatePartner',
                      input: { id: partner.id, logo: uploadedLogoUrl }
                    })
                    if (refetchPartner) await refetchPartner()
                  }
                }
                setAssistantIsTyping(false)
                sendNameInputMessage()
              },
              onDecline: () => {
                localStorage.setItem(DECLINE_STORAGE_KEY, '1')
                sendNameInputMessage()
              }
            }
          })
        }, 1500)
      }

      // START

      // Check if brand has valid searchData before showing adoption message
      const hasValidBrandData =
        brand?.searchData &&
        Array.isArray(brand.searchData) &&
        brand.searchData.length > 0

      if (shouldShowCompanyProfileAdoption && hasValidBrandData) {
        await sendCompanyProfileAdoptionMessage(brand)
      } else if (!hasName) {
        sendNameInputMessage()
      } else {
        sendRequirementProfileIntroMessage()
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    variant,
    hasCompletedOnboarding,
    activeSpace,
    enableAssistant,
    setAssistantIsTyping,
    sendMessageToAssistant,
    run,
    sendAssessmentPreviewMessage
  ])
}
